import Close from '@mui/icons-material/Close';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SxProps } from '@mui/system';
import { t } from 'i18next';
import { snakeCase } from 'lodash-es';
import { useCallback, useMemo } from 'react';

import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import useDispatchNumberFilterLabel from '~hooks/useDispatchNumberFilterLabel';
import { hexToRgba } from '~utils/utilFunctions';

import { FilterMenuOption } from './FilterMenuItem';

const CHIP_MAX_ITEMS = 1;
const CHIP_MAX_WIDTH_IN_PX = 270;
const MAX_VISIBLE_FILTERS = 1;

const DISPATCH_NUMBERS_FILTER = 'dispatchNumberIds';

export type AppliedFiltersChipsCollectedData = Record<string, FilterMenuOption[]>;

interface AppliedFiltersChipsProps {
  appliedFilters: Record<string, string[]>;
  collectedData: AppliedFiltersChipsCollectedData;
  excludedFilters?: string[];
  maxVisibleFilters?: number;
  onRemoveAllFilters: (filters: string[]) => void;
  onRemoveFilter: (filter: string) => void;
  sx?: SxProps;
}

export default function AppliedFiltersChips({
  appliedFilters,
  collectedData,
  excludedFilters,
  maxVisibleFilters = MAX_VISIBLE_FILTERS,
  onRemoveAllFilters,
  onRemoveFilter,
  sx,
}: AppliedFiltersChipsProps) {
  const theme = useTheme();
  const resolveFilterLabel = useDispatchNumberFilterLabel();
  const effectiveFilters = useMemo(() => {
    return Object.entries(appliedFilters).reduce(
      (acc, [filter, filterValues]) => {
        const selections = filterValues ?? [];
        const isExcluded = excludedFilters?.includes(filter);

        if (selections.length > 0 && !isExcluded) {
          acc.push({ filter, selections });
        }

        return acc;
      },
      [] as Array<{ filter: string; selections: string[] }>,
    );
  }, [appliedFilters, excludedFilters]);

  const [visibleFilters, hiddenFilters] = [
    effectiveFilters
      .slice(0, maxVisibleFilters)
      .sort((a, b) => a.filter.localeCompare(b.filter)),
    effectiveFilters
      .slice(maxVisibleFilters)
      .sort((a, b) => a.filter.localeCompare(b.filter)),
  ];

  const hasFiltersApplied = effectiveFilters.length > 0;

  const handleRemoveAllFilters = useCallback(() => {
    const filters = effectiveFilters.reduce((acc, effectiveFilter) => {
      return acc.concat([effectiveFilter.filter]);
    }, [] as string[]);

    onRemoveAllFilters(filters);
  }, [effectiveFilters, onRemoveAllFilters]);

  if (!hasFiltersApplied) {
    return null;
  }

  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'center',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {visibleFilters.map(({ filter, selections }) => (
        <AppliedFilterChip
          key={filter}
          filter={filter}
          hasRightSibling={hiddenFilters.length > 0}
          onRemoveFilter={onRemoveFilter}
          selections={selections}
          values={collectedData[filter] ?? []}
        />
      ))}
      {hiddenFilters.length > 0 && (
        <>
          <Menu
            sx={{ '& .MuiPaper-root': { minWidth: '250px' } }}
            menuTrigger={
              <SmallButton
                size="small"
                sx={{
                  '&.MuiButton-root': {
                    backgroundColor: theme.semantic.surfaceRaised,
                    color: theme.palette.text.primary,
                    borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: theme.brandV2.borderRadius,
                    borderLeft: 0,
                    borderRight: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                    borderTop: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                    borderTopLeftRadius: 0,
                    borderTopRightRadius: theme.brandV2.borderRadius,
                    fontWeight: 400,
                    px: 1,
                  },
                }}
              >
                {maxVisibleFilters === 0
                  ? t('dispatch.dispatch_v2.filters.filters_', {
                      count: hiddenFilters.length,
                    })
                  : t('dispatch.dispatch_v2.filters.more_filters', {
                      count: hiddenFilters.length,
                    })}
              </SmallButton>
            }
          >
            {hiddenFilters.map(({ filter }) => (
              <MenuItem
                key={filter}
                disableRipple
                sx={{
                  '&.MuiMenuItem-root.MuiButtonBase-root': {
                    '&:hover': { backgroundColor: theme.semantic.surfaceRaised },
                    '&.Mui-focusVisible': {
                      backgroundColor: theme.semantic.surfaceRaised,
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Typography color={theme.brandV2.colors.treadBlack} variant="subtitle2">
                    {filter === DISPATCH_NUMBERS_FILTER
                      ? resolveFilterLabel(true, '')
                      : t(`dispatch.dispatch_v2.filters.${snakeCase(filter)}`)}
                  </Typography>

                  <Close
                    onClick={() => onRemoveFilter(filter)}
                    sx={{ fontSize: '16px' }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Menu>

          <Tooltip title={t('dispatch.dispatch_v2.filters.clear_all_filters')}>
            <IconButton
              onClick={handleRemoveAllFilters}
              size="small"
              sx={{
                backgroundColor: hexToRgba(theme.brandV2.colors.treadBlack, 0.04),
                ml: 1,
              }}
            >
              <Close sx={{ fontSize: '16px', color: theme.brandV2.colors.treadBlack }} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Box>
  );
}

const labelBySelectedFilter: Record<string, string> = {
  'connected_company.company_types': t('dispatch.dispatch_v2.filters.company_types'),
  category: t('dispatch.dispatch_v2.filters.driver_type'),
  collaboratorIds: t('projects.filters.collaborators'),
  customerAccountIds: t('dispatch.dispatch_v2.filters.customers'),
  customer_account_ids: t('dispatch.dispatch_v2.filters.customers'),
  customers: t('dispatch.dispatch_v2.filters.customers'),
  departments: t('dispatch.dispatch_v2.filters.departments'),
  departmentIds: t('dispatch.dispatch_v2.filters.departments'),
  dispatchNumberIds: t('dispatch.dispatch_v2.filters.dispatch_numbers'),
  dispatchStatuses: t('dispatch.dispatch_v2.filters.dispatch_statuses'),
  driverIds: t('dispatch.dispatch_v2.filters.drivers'),
  dropOffSiteIds: t('dispatch.dispatch_v2.filters.drop_off_sites'),
  dropOffSites: t('dispatch.dispatch_v2.filters.drop_off_sites'),
  dropoffSiteIds: t('dispatch.dispatch_v2.filters.drop_off_sites'),
  dropoffSites: t('dispatch.dispatch_v2.filters.drop_off_sites'),
  dropoffSitesIds: t('dispatch.dispatch_v2.filters.drop_off_sites'),
  dropoff_site_ids: t('dispatch.dispatch_v2.filters.drop_off_sites'),
  dropoff_sites_ids: t('dispatch.dispatch_v2.filters.drop_off_sites'),
  equipmentIds: t('dispatch.dispatch_v2.filters.equipment'),
  equipmentOwnerCompanyIds: t('dispatch.dispatch_v2.filters.equipment_owners'),
  equipmentTypeIds: t('dispatch.dispatch_v2.filters.equipment_types'),
  equipmentTypes: t('dispatch.dispatch_v2.filters.equipment_types'),
  foremanIds: t('dispatch.dispatch_v2.filters.foremen'),
  foremen: t('dispatch.dispatch_v2.filters.foremen'),
  jobStates: t('dispatch.dispatch_v2.filters.job_states'),
  labels: t('dispatch.dispatch_v2.filters.labels'),
  materialIds: t('dispatch.dispatch_v2.filters.materials'),
  orderStates: t('dispatch.dispatch_v2.filters.refined_order_states'),
  pickUpSiteIds: t('dispatch.dispatch_v2.filters.pick_up_sites'),
  pickUpSites: t('dispatch.dispatch_v2.filters.pick_up_sites'),
  pickupSiteIds: t('dispatch.dispatch_v2.filters.pick_up_sites'),
  pickupSitesIds: t('dispatch.dispatch_v2.filters.pick_up_sites'),
  pickup_site_ids: t('dispatch.dispatch_v2.filters.pick_up_sites'),
  pickup_sites_ids: t('dispatch.dispatch_v2.filters.pick_up_sites'),
  prevailingWage: t('dispatch.dispatch_v2.filters.prevailing_wage'),
  prevailing_wage: t('dispatch.dispatch_v2.filters.prevailing_wage'),
  projectExternalIds: t('dispatch.dispatch_v2.filters.projects_external_ids'),
  projectIds: t('dispatch.dispatch_v2.filters.projects'),
  project_ids: t('dispatch.dispatch_v2.filters.project_ids'),
  projects: t('dispatch.dispatch_v2.filters.projects'),
  projectsExternalIds: t('dispatch.dispatch_v2.filters.projects_external_ids'),
  rateTypes: t('form_fields.rate_type'),
  rate_types: t('form_fields.rate_type'),
  refinedJobStates: t('dispatch.dispatch_v2.filters.job_states'),
  refinedOrderStates: t('dispatch.dispatch_v2.filters.refined_order_states'),
  requesterIds: t('dispatch.dispatch_v2.filters.requesters'),
  roles: t('dispatch.dispatch_v2.filters.roles'),
  salespersonIds: t('projects.filters.salespersons'),
  senderCompanies: t('dispatch.dispatch_v2.filters.sender_companies'),
  senderCompanyTypes: t('dispatch.dispatch_v2.filters.sender_company_types'),
  siteTypes: t('dispatch.dispatch_v2.filters.site_types'),
  states: t('projects.filters.states'),
  vendorAccountIds: t('dispatch.dispatch_v2.filters.vendors'),
  vendor_account_ids: t('dispatch.dispatch_v2.filters.vendors'),
  visibility: t('dispatch.dispatch_v2.filters.visibility'),
};

interface AppliedFilterChipProps {
  filter: string;
  hasRightSibling?: boolean;
  onRemoveFilter: AppliedFiltersChipsProps['onRemoveFilter'];
  selections: string[];
  values: FilterMenuOption[];
}

function AppliedFilterChip({
  filter,
  hasRightSibling,
  onRemoveFilter,
  selections,
  values,
}: AppliedFilterChipProps) {
  const theme = useTheme();
  const resolveFilterLabel = useDispatchNumberFilterLabel();
  const label = resolveFilterLabel(
    filter === DISPATCH_NUMBERS_FILTER,
    labelBySelectedFilter[filter],
  );
  const selectionsContent = values
    .filter(({ value }) => selections.includes(value))
    .map(({ label }) => label);

  // Show fallback text when filters are active but labels can't be resolved
  const hasUnresolvedFilters = selections.length > 0 && selectionsContent.length === 0;
  const effectiveContent = hasUnresolvedFilters
    ? [
        t('dispatch.dispatch_v2.filters.count_selected', {
          count: selections.length,
        }),
      ]
    : selectionsContent;

  const content = effectiveContent.slice(0, CHIP_MAX_ITEMS);
  const nonVisibleItemsCount = effectiveContent.length - CHIP_MAX_ITEMS;
  const handleRemoveFilterClick = useCallback(() => {
    onRemoveFilter(filter);
  }, [filter]);

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: theme.semantic.surfaceRaised,
        borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        borderLeft: 0,
        borderRight: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        borderTop: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        display: 'flex',
        height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
        maxWidth: CHIP_MAX_WIDTH_IN_PX,
        px: 1,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: hasRightSibling ? 0 : theme.brandV2.borderRadius,
        borderTopLeftRadius: 0,
        borderTopRightRadius: hasRightSibling ? 0 : theme.brandV2.borderRadius,
      }}
    >
      <Typography
        color={theme.brandV2.colors.treadGray3}
        sx={{
          flex: 0,
          fontSize: '12px',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      <Typography
        color={theme.brandV2.colors.treadBlack}
        sx={{
          flex: 1,
          fontSize: '12px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          pl: 1,
        }}
      >
        {content.join(', ')}
      </Typography>
      {nonVisibleItemsCount > 0 && (
        <Typography
          color={theme.brandV2.colors.treadBlack}
          sx={{
            flex: 0,
            fontSize: '12px',
            whiteSpace: 'nowrap',
          }}
        >
          {`, +${nonVisibleItemsCount}`}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          flex: 0,
          pl: 1,
        }}
      >
        <Close
          onClick={handleRemoveFilterClick}
          sx={{
            color: theme.brandV2.colors.treadBlack,
            cursor: 'pointer',
            fontSize: '16px',
          }}
        />
      </Box>
    </Box>
  );
}
